import React, { useState, useEffect } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Package, CheckCircle, XCircle, Loader2 } from 'lucide-react';
import api from '../utils/api';

const VerifyEmailPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  
  const [status, setStatus] = useState('verifying'); // verifying, success, error
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    if (!token) {
      return;
    }

    const verifyEmail = async () => {
      try {
        await api.post('/auth/verify-email', { token });
        setStatus('success');
      } catch (err) {
        setStatus('error');
        setErrorMessage(err.response?.data?.detail || 'Verification failed. The link may have expired.');
      }
    };

    verifyEmail();
  }, [token]);

  // Handle missing token separately
  if (!token && status === 'verifying') {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="flex justify-center mb-8">
            <div className="flex items-center gap-3">
              <div className="w-14 h-14 bg-primary rounded-xl flex items-center justify-center">
                <Package className="text-white" size={32} />
              </div>
              <h1 className="text-3xl font-bold gradient-text">MoveMate</h1>
            </div>
          </div>

          <Card className="glassmorphism border-2 border-white/50 shadow-xl" data-testid="verify-email-card">
            <CardContent className="pt-8 pb-8 text-center">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <XCircle className="text-red-600" size={32} />
              </div>
              <h2 className="text-2xl font-bold mb-2">Invalid Link</h2>
              <p className="text-gray-600 mb-6">Please check your email for the correct verification link.</p>
              <Link to="/login">
                <Button className="w-full" data-testid="go-to-login-btn">
                  Go to Login
                </Button>
              </Link>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const renderContent = () => {
    switch (status) {
      case 'verifying':
        return (
          <CardContent className="pt-8 pb-8 text-center">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Loader2 className="text-blue-600 animate-spin" size={32} />
            </div>
            <h2 className="text-2xl font-bold mb-2">Verifying Your Email</h2>
            <p className="text-gray-600">Please wait while we verify your email address...</p>
          </CardContent>
        );
      
      case 'success':
        return (
          <CardContent className="pt-8 pb-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="text-green-600" size={32} />
            </div>
            <h2 className="text-2xl font-bold mb-2">Email Verified!</h2>
            <p className="text-gray-600 mb-6">
              Your email has been successfully verified. You can now log in to your MoveMate account.
            </p>
            <Button className="w-full" onClick={() => navigate('/login')} data-testid="go-to-login-btn">
              Go to Login
            </Button>
          </CardContent>
        );
      
      case 'error':
        return (
          <CardContent className="pt-8 pb-8 text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <XCircle className="text-red-600" size={32} />
            </div>
            <h2 className="text-2xl font-bold mb-2">Verification Failed</h2>
            <p className="text-gray-600 mb-6">{errorMessage}</p>
            <div className="space-y-3">
              <Link to="/login">
                <Button className="w-full" data-testid="go-to-login-btn">
                  Go to Login
                </Button>
              </Link>
              <p className="text-sm text-gray-500">
                Need a new verification link?{' '}
                <Link to="/login" className="text-primary hover:underline">
                  Log in to request one
                </Link>
              </p>
            </div>
          </CardContent>
        );
      
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="flex justify-center mb-8">
          <div className="flex items-center gap-3">
            <img src="/logo.png" alt="MoveMate" className="h-14" />
            <h1 className="text-3xl font-bold gradient-text">MoveMate</h1>
          </div>
        </div>

        <Card className="glassmorphism border-2 border-white/50 shadow-xl" data-testid="verify-email-card">
          {renderContent()}
        </Card>
      </div>
    </div>
  );
};

export default VerifyEmailPage;
